import * as React from 'react';
import { ArrowLeft, Home, RefreshCw, ShieldAlert } from 'lucide-react';
import { useRouteError } from 'react-router-dom';

import { getErrorPresentation } from './route-error-utils';
import './route-error-page.css';

export function RouteErrorPage(): React.JSX.Element {
  const error = useRouteError();
  const presentation = getErrorPresentation(error);
  const [reference] = React.useState(() => createErrorReference());

  React.useEffect(() => {
    // Keep the complete error available to local logs and future monitoring integrations.
    console.error(`[RouteError:${reference}]`, error);
  }, [error, reference]);

  return (
    <main className="route-error-page">
      <div className="route-error-page__glow" aria-hidden="true" />
      <section className="route-error-card" aria-labelledby="route-error-title">
        <div className="route-error-brand" aria-label="Pulse">
          <span className="route-error-brand__mark" aria-hidden="true">P</span>
          <span>Pulse</span>
        </div>

        <div className="route-error-icon" aria-hidden="true">
          <ShieldAlert />
        </div>

        <p className="route-error-eyebrow">Something interrupted this page</p>
        <h1 id="route-error-title">{presentation.title}</h1>
        <p className="route-error-message">{presentation.message}</p>

        <div className="route-error-actions">
          <button type="button" className="pulse-btn accent" onClick={() => window.location.reload()}>
            <RefreshCw />Try again
          </button>
          <a className="pulse-btn" href="/dashboard">
            <Home />Go to dashboard
          </a>
          <button type="button" className="pulse-btn ghost" onClick={() => window.history.back()}>
            <ArrowLeft />Go back
          </button>
        </div>

        <div className="route-error-support">
          <span>If this keeps happening, share this reference with your administrator.</span>
          <code>{reference}</code>
        </div>

        {import.meta.env.DEV && presentation.details ? (
          <details className="route-error-details">
            <summary>Developer details</summary>
            <pre>{presentation.details}</pre>
          </details>
        ) : null}
      </section>
      <p className="route-error-footer">Retry this page or return to the dashboard to continue.</p>
    </main>
  );
}

function createErrorReference(): string {
  const timestamp = Date.now().toString(36).toUpperCase();
  const suffix = Math.random().toString(36).slice(2, 7).toUpperCase();
  return `ERR-${timestamp}-${suffix}`;
}
